import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link, useParams } from 'react-router-dom';
import './ResultsFrameworkPage.css';
import SingleSlide from '../../components/contentElements/SingleSlide';
import Blockquote from '../../components/contentElements/Blockquote';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

function ResultsFrameworkPage() {
  const { pilarId } = useParams();

  const [frameworks, setFrameworks] = useState([]);
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFrameworks = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/result_frameworks/active`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setFrameworks(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFrameworks();
  }, []);

  const text = (item, field) =>
    item?.[`${field}_${language}`] ||
    item?.[`${field}_fr`] ||
    item?.[`${field}_en`] ||
    '';

  const labels = {
    framework:
      language === 'fr'
        ? 'Cadre de Résultats ACE 2026-2030'
        : 'ACE Results Framework 2026-2030',

    overview:
      language === 'fr'
        ? 'Cadre stratégique structuré autour de trois piliers.'
        : 'Strategic framework structured around three pillars.',

    pillar:
      language === 'fr'
        ? 'Pilier'
        : 'Pillar',

    impact:
      language === 'fr'
        ? 'Impact'
        : 'Impact',

    indicators:
      language === 'fr'
        ? 'Indicateurs'
        : 'Indicators',

    indicator:
      language === 'fr'
        ? 'Indicateur'
        : 'Indicator',

    outcomes:
      language === 'fr'
        ? 'Effets'
        : 'Outcomes',

    outputs:
      language === 'fr'
        ? 'Produits'
        : 'Outputs',

    code:
      language === 'fr'
        ? 'Code'
        : 'Code',

    target:
      language === 'fr'
        ? 'Cible'
        : 'Target',

    targetYear:
      language === 'fr'
        ? 'Année cible'
        : 'Target year',

    baseline:
      language === 'fr'
        ? 'Année de référence'
        : 'Baseline year',

    back:
      language === 'fr'
        ? 'Vue d’ensemble'
        : 'Framework overview',

    previous:
      language === 'fr'
        ? 'Pilier précédent'
        : 'Previous pillar',

    next:
      language === 'fr'
        ? 'Pilier suivant'
        : 'Next pillar',

    details:
      language === 'fr'
        ? 'Voir le cadre détaillé'
        : 'View detailed framework',
  };

  const displayedFrameworks = pilarId
    ? frameworks.filter(
        (framework) =>
          framework.pilar_id === Number(pilarId)
      )
    : frameworks;

  const indicatorDetails = (indicator) => (
    <div className="rf-indicator-details">
      <div className="rf-indicator-description">
        {text(indicator, 'description')}
      </div>

      {indicator.baseline_year && (
        <div>
          <strong>{labels.baseline} :</strong>{' '}
          {indicator.baseline_year}
        </div>
      )}

      {indicator.target_value && (
        <div>
          <strong>{labels.target} :</strong>{' '}
          {indicator.target_value}
        </div>
      )}

      {indicator.target_year && (
        <div>
          <strong>{labels.targetYear} :</strong>{' '}
          {indicator.target_year}
        </div>
      )}
    </div>
  );

  /*
   * GLOBAL OVERVIEW
   */

  const renderSimpleIndicators = (indicators = []) => {
    if (!indicators.length) return null;

    return (
      <div className="mt-3">
        <h6>{labels.indicators}</h6>

        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead>
              <tr>
                <th>{labels.code}</th>
                <th>{labels.indicator}</th>
                <th>{labels.target}</th>
                <th>{labels.targetYear}</th>
              </tr>
            </thead>

            <tbody>
              {indicators.map((indicator) => (
                <tr key={indicator.id}>
                  <td>{indicator.code}</td>
                  <td>{text(indicator, 'description')}</td>
                  <td>{indicator.target_value || '—'}</td>
                  <td>{indicator.target_year || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSimpleResult = (result, depth = 0) => (
    <div
      key={result.id}
      className={`mb-4 ${
        depth > 0 ? 'ms-3 ms-md-4' : ''
      }`}
    >
      <div className="card">
        <div className="card-body">
          <div className="small text-muted mb-1">
            {result.code} · {result.level}
          </div>

          {text(result, 'title') && (
            <h5 className="card-title">
              {text(result, 'title')}
            </h5>
          )}

          <p className="card-text">
            {text(result, 'description')}
          </p>

          {renderSimpleIndicators(result.indicators)}
        </div>
      </div>

      {result.children?.length > 0 && (
        <div className="mt-3">
          {result.children.map((child) =>
            renderSimpleResult(child, depth + 1)
          )}
        </div>
      )}
    </div>
  );

  const renderGlobalOverview = () => (
    <main className="container py-5">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-5">
        <div>
          <h1>{labels.framework}</h1>

          <p className="lead mb-0">
            {labels.overview}
          </p>
        </div>

      </div>

      {frameworks.map((framework) => (
        <section
          key={framework.id}
          className="mb-5"
        >
          <div className="mb-4">
            <div className="text-uppercase small fw-bold text-muted">
              {labels.pillar} {framework.pilar_id}
            </div>

            <h2>
              {text(framework, 'title')}
            </h2>

            <p>
              {text(framework, 'description')}
            </p>

            <Link
              to={`/results-framework/pillar/${framework.pilar_id}`}
              className="btn btn-outline-primary"
            >
              {labels.details} →
            </Link>
          </div>

          {framework.results?.map((result) =>
            renderSimpleResult(result)
          )}
        </section>
      ))}
    </main>
  );

  /*
   * DETAILED PILLAR VIEW
   */

  const renderImpactIndicators = (indicators = []) => {
    if (!indicators.length) return null;

    return (
      <div className="rf-impact-indicators">
        {indicators.map((indicator) => (
          <article
            className="rf-impact-indicator"
            key={indicator.id}
          >
            <div className="rf-impact-indicator-code">
              {labels.indicator} {indicator.code}
            </div>

            <div className="rf-impact-indicator-body">
              {indicatorDetails(indicator)}
            </div>
          </article>
        ))}
      </div>
    );
  };

  const renderOutputIndicators = (indicators = []) => {
    if (!indicators.length) {
      return <span className="rf-empty">—</span>;
    }

    return (
      <div className="rf-output-indicators">
        {indicators.map((indicator) => (
          <div
            className="rf-output-indicator"
            key={indicator.id}
          >
            <div className="rf-code">
              {labels.indicator} {indicator.code}
            </div>

            {indicatorDetails(indicator)}
          </div>
        ))}
      </div>
    );
  };

  const renderOutcomeRows = (impact) => {
    const outcomes = impact.children || [];

    return outcomes.map((outcome) => {
      const outputs = outcome.children || [];

      if (!outputs.length) {
        return (
          <div
            className="rf-matrix-row"
            key={outcome.id}
          >
            <div className="rf-outcome-cell">
              <div className="rf-code">
                {outcome.code}
              </div>

              <h4>
                {text(outcome, 'title')}
              </h4>

              <p>
                {text(outcome, 'description')}
              </p>

              {outcome.indicators?.length > 0 && (
                <div className="rf-outcome-indicators">
                  {outcome.indicators.map(
                    (indicator) => (
                      <div
                        className="rf-outcome-indicator"
                        key={indicator.id}
                      >
                        <div className="rf-code">
                          {labels.indicator}{' '}
                          {indicator.code}
                        </div>

                        {indicatorDetails(indicator)}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <div className="rf-output-cell">
              <span className="rf-empty">—</span>
            </div>

            <div className="rf-indicators-cell">
              <span className="rf-empty">—</span>
            </div>
          </div>
        );
      }

      return (
        <div
          className="rf-outcome-group"
          key={outcome.id}
        >
          <div className="rf-outcome-cell">
            <div className="rf-code">
              {outcome.code}
            </div>

            <h4>
              {text(outcome, 'title')}
            </h4>

            <p>
              {text(outcome, 'description')}
            </p>

            {outcome.indicators?.length > 0 && (
              <div className="rf-outcome-indicators">
                {outcome.indicators.map(
                  (indicator) => (
                    <div
                      className="rf-outcome-indicator"
                      key={indicator.id}
                    >
                      <div className="rf-code">
                        {labels.indicator}{' '}
                        {indicator.code}
                      </div>

                      {indicatorDetails(indicator)}
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          <div className="rf-output-indicator-stack">
            {outputs.map((output) => (
              <div
                className="rf-output-indicator-row"
                key={output.id}
              >
                <div className="rf-output-cell">
                  <div className="rf-code">
                    {output.code}
                  </div>

                  <h4>
                    {text(output, 'title')}
                  </h4>

                  <p>
                    {text(output, 'description')}
                  </p>
                </div>

                <div className="rf-indicators-cell">
                  {renderOutputIndicators(
                    output.indicators
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  const renderDetailedFramework = (framework) => {
    const impacts =
      framework.results?.filter(
        (result) => result.level === 'impact'
      ) || [];

    return (
      <section
        className="rf-framework"
        key={framework.id}
      >
        <header className="rf-framework-heading">
          <div className="rf-pillar-label">
            {labels.pillar} {framework.pilar_id}
          </div>

          <h2>
            {text(framework, 'title')}
          </h2>

          <p className="rf-strategic-objective">
            {text(framework, 'description')}
          </p>
        </header>

        {impacts.map((impact) => (
          <div
            className="rf-impact-section"
            key={impact.id}
          >
            <div className="rf-impact-box">
              <div className="rf-impact-badge">
                <span className="rf-impact-icon">
                  ◎
                </span>

                <span>
                  {labels.impact}
                </span>
              </div>

              <div className="rf-impact-content">
                <div className="rf-code">
                  {impact.code}
                </div>

                <h3>
                  {text(impact, 'title')}
                </h3>

                <p>
                  {text(impact, 'description')}
                </p>
              </div>
            </div>

            {renderImpactIndicators(
              impact.indicators
            )}

            <div className="rf-matrix">
              <div className="rf-matrix-header">
                <div>
                  {labels.outcomes}
                </div>

                <div>
                  {labels.outputs}
                </div>

                <div>
                  {labels.indicators}
                </div>
              </div>

              {renderOutcomeRows(impact)}
            </div>
          </div>
        ))}

        <nav
          className="rf-navigation"
          aria-label="Results framework navigation"
        >
          <Link
            className="rf-nav-button rf-nav-secondary"
            to="/results-framework"
          >
            ← {labels.back}
          </Link>

          <div className="rf-navigation-right">
            {Number(pilarId) > 1 && (
              <Link
                className="rf-nav-button rf-nav-secondary"
                to={`/results-framework/pillar/${
                  Number(pilarId) - 1
                }`}
              >
                ← {labels.previous}
              </Link>
            )}

            {Number(pilarId) < 3 && (
              <Link
                className="rf-nav-button rf-nav-primary"
                to={`/results-framework/pillar/${
                  Number(pilarId) + 1
                }`}
              >
                {labels.next} →
              </Link>
            )}
          </div>
        </nav>
      </section>
    );
  };

  const renderPillarView = () => (
    <main className="rf-page">
      <div className="rf-page-top">
        <div>
          <h1>
            {labels.framework}
          </h1>
        </div>

      </div>

      {displayedFrameworks.length === 0 ? (
        <div className="alert alert-warning">
          {language === 'fr'
            ? 'Aucun cadre de résultats trouvé pour ce pilier.'
            : 'No results framework found for this pillar.'}
        </div>
      ) : (
        displayedFrameworks.map(
          renderDetailedFramework
        )
      )}
    </main>
  );

  /*
   * LOADING / ERROR
   */

  if (loading) {
    return (
      <div className="rf-status">
        {language === 'fr'
          ? 'Chargement du cadre de résultats...'
          : 'Loading results framework...'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rf-status">
        <div className="alert alert-danger">
          {language === 'fr'
            ? `Impossible de charger le cadre de résultats : ${error}`
            : `Unable to load the results framework: ${error}`}
        </div>
      </div>
    );
  }

  /*
   * CHOOSE VIEW
   */

  return (
    <>
      <SingleSlide />
      <Blockquote />

      {!pilarId
        ? renderGlobalOverview()
        : renderPillarView()}
    </>
  );
}

export default ResultsFrameworkPage;
